import { useState } from 'react'
import { useExperience } from '@mc/experience'
import { useIsEnterpriseMember } from 'pages/certificates/common'
import {
  EmailCapture,
  EngagementActionBlock,
  EngagementNotice,
  EngagementPromo,
  TrackingRow,
} from 'pages/certificates/components'
import { DownloadSyllabusModal } from 'pages/certificates/components/DownloadSyllabus'
import { TrackingAttributes } from 'pages/certificates/tracking'
import { HeroVariant } from '../HeroVariant'
import { LearnMoreLink } from '../LearnMoreLink'
import { SubscriptionNote } from '../SubscriptionNote'
import { type HeroVariantProps } from '../types'

// HeroVariant3 mirrors HeroSection's full engagement switch and adds a SubscriptionNote below it,
// so it stays a clean drop-in for HeroSection at productization. emailCapture/notice
// can't occur during the experiment (useIsSubscriptionsExpEligible excludes them) but
// are kept in sync with HeroSection on purpose.

const getEngagementToShow = (
  promo: boolean,
  emailCapture: boolean,
  notice: boolean,
  isEnterprise: boolean,
) => {
  if (isEnterprise) return { showEngagement: true }
  if (promo) return { showPromo: true }
  if (emailCapture) return { showEmailCapture: true }
  if (notice) return { showNotice: true }
  return { showEngagement: true }
}

export const HeroVariant3 = (props: HeroVariantProps) => {
  const {
    program,
    available = false,
    engagement,
    certUuid,
    certSlug,
    promoVariables,
  } = props
  const isEnterprise = useIsEnterpriseMember()
  const { isReady } = useExperience()

  const [showSyllabusModal, setShowSyllabusModal] = useState(false)

  const finalEngagement = engagement ?? program.defaultEngagement

  const { showPromo, showEmailCapture, showNotice, showEngagement } =
    getEngagementToShow(
      !!promoVariables?.cta,
      !!finalEngagement?.emailCapture,
      !!finalEngagement?.notice,
      isEnterprise,
    )

  return (
    <TrackingRow
      row_name='hero-simple-sub'
      location='hero-simple-sub'
      visibilityConfig={{ threshold: 0.01 }}
    >
      <>
        <HeroVariant {...props}>
          <div className='flex flex-col items-center lg:items-start gap-4'>
            {isReady && showPromo && promoVariables?.cta && (
              <div>
                <EngagementPromo
                  promo={promoVariables.promo}
                  cta={promoVariables.cta}
                  releaseLabel={program.releaseLabel}
                  setShowSyllabusModal={setShowSyllabusModal}
                  showSyllabusButton={!!program.syllabus?.pdfUrl}
                  certUuid={certUuid}
                  certSlug={certSlug}
                  available={available}
                  defaultEngagement={program.defaultEngagement?.actionBlock}
                  engagement={engagement?.actionBlock}
                />
              </div>
            )}
            {/* EmailCapture and EngagementNotice won't render in the experiment (useIsSubscriptionsExpEligible excludes those states), left here for parity with HeroSection's conditionals */}
            {isReady && showEmailCapture && finalEngagement && (
              <EmailCapture emailCapture={finalEngagement.emailCapture} />
            )}
            {isReady && showNotice && finalEngagement && (
              <EngagementNotice notice={finalEngagement.notice} />
            )}
            {isReady && showEngagement && (
              <EngagementActionBlock
                defaultEngagement={program.defaultEngagement?.actionBlock}
                engagement={engagement?.actionBlock}
                certUuid={certUuid}
                setShowSyllabusModal={setShowSyllabusModal}
              />
            )}
            <SubscriptionNote learnMoreLink={<LearnMoreLink />} />
          </div>
        </HeroVariant>
        {/* Syllabus button/modal mirrors HeroSection. mccerts-1928 shipped variant_2
          (hide secondary CTA) as the winner, so the button is hidden in production;
          the wiring stays for parity, removable when that experiment is cleaned up. */}
        {program.syllabus?.pdfUrl && (
          <TrackingAttributes
            resource_link={program.syllabus.pdfUrl}
            resource_title={program.title}
          >
            <DownloadSyllabusModal
              show={showSyllabusModal}
              onClose={() => setShowSyllabusModal(false)}
              syllabusData={{
                syllabusPreviewImageUrl: program.syllabus.preview10x13?.url,
                title: program.title,
                syllabusPdfUrl: program.syllabus.pdfUrl,
              }}
            />
          </TrackingAttributes>
        )}
      </>
    </TrackingRow>
  )
}
